'use client';

import { useState } from 'react';

// Helper to get nested object values (e.g., "metadata.brandCode")
const getNestedValue = (obj: any, path: string) => {
  return path.split('.').reduce((current, key) => current?.[key], obj);
};

// Generic sorting function
export const sortData = (data: any[], sortKey: string, direction: 'asc' | 'desc') => {
  return [...data].sort((a, b) => {
    const aValue = getNestedValue(a, sortKey);
    const bValue = getNestedValue(b, sortKey);

    // Handle null/undefined values
    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return direction === 'asc' ? -1 : 1;
    if (bValue == null) return direction === 'asc' ? 1 : -1;

    // Handle different data types
    let comparison = 0;

    // Check if values are dates
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      const aDate = new Date(aValue);
      const bDate = new Date(bValue);
      if (!Number.isNaN(aDate.getTime()) && !Number.isNaN(bDate.getTime())) {
        comparison = aDate.getTime() - bDate.getTime();
      } else {
        // String comparison
        comparison = aValue.localeCompare(bValue);
      }
    }
    // Number comparison
    else if (typeof aValue === 'number' && typeof bValue === 'number') {
      comparison = aValue - bValue;
    }
    // Default string comparison
    else {
      comparison = String(aValue).localeCompare(String(bValue));
    }

    return direction === 'asc' ? comparison : -comparison;
  });
};

// Custom hook for managing sort state
export const useSorting = (initialSort?: { key: string; direction: 'asc' | 'desc' }) => {
  const [sort, setSort] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>(initialSort || null);

  const handleSort = (key: string) => {
    setSort((currentSort) => {
      if (currentSort?.key === key) {
        // Toggle direction if same key
        return {
          key,
          direction: currentSort.direction === 'asc' ? 'desc' : 'asc',
        };
      } else {
        // New key, default to ascending
        return { key, direction: 'asc' };
      }
    });
  };

  return { sort, handleSort, setSort };
};
